import { useState, useRef, useCallback } from "react";
import {
    Box,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import {
    PropertyComposite,
} from "grapesjs";
import DebouncedTextField from "features/common/components/DebouncedTextField";

const BoxSide = ({
    label,
    isActive,
    position,
    type,
    handleSideClick
}: {
    label: string;
    isActive: boolean;
    position: 'top' | 'right' | 'bottom' | 'left';
    type: 'margin' | 'padding';
    handleSideClick: (side: 'margin' | 'padding', position: 'top' | 'right' | 'bottom' | 'left') => void;
}) => {
    const theme = useTheme();
    const getPosition = () => {
        if (type === 'margin') {
            // Margin labels go outside the margin box
            return {
                ...(position === 'top' && { top: 3, left: '50%', transform: 'translateX(-50%)' }),
                ...(position === 'right' && { right: -20, top: '50%', transform: 'translateY(-50%)' }),
                ...(position === 'bottom' && { bottom: 3, left: '50%', transform: 'translateX(-50%)' }),
                ...(position === 'left' && { left: -20, top: '50%', transform: 'translateY(-50%)' }),
            };
        } else {
            // Padding labels go between margin and padding boxes
            return {
                ...(position === 'top' && { top: 30, left: '50%', transform: 'translateX(-50%)' }),
                ...(position === 'right' && { right: 35, top: '50%', transform: 'translateY(-50%)' }),
                ...(position === 'bottom' && { bottom: 30, left: '50%', transform: 'translateX(-50%)' }),
                ...(position === 'left' && { left: 35, top: '50%', transform: 'translateY(-50%)' }),
            };
        }
    };

    return (
        <Box
            sx={{
                position: 'absolute',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 50,
                minHeight: 24,
                backgroundColor: isActive ? theme.palette.primary.main : theme.palette.grey[100],
                color: isActive ? 'white' : theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: isActive ? 'bold' : 'normal',
                transition: 'all 0.2s',
                '&:hover': {
                    backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.grey[200],
                },
                ...getPosition(),
            }}
            onClick={() => handleSideClick(type, position)}
        >
            {label}
        </Box>
    );
};

interface IBoxModelEditorProps {
    marginProp: PropertyComposite;
    paddingProp: PropertyComposite;
}

const BoxModelEditor = ({
    marginProp,
    paddingProp,
}: IBoxModelEditorProps) => {
    const theme = useTheme();
    const [activeSide, setActiveSide] = useState<'margin' | 'padding' | null>(null);
    const [activePosition, setActivePosition] = useState<'top' | 'right' | 'bottom' | 'left' | null>(null);

    // Refs for text fields
    const topFieldRef = useRef<HTMLInputElement>(null);
    const rightFieldRef = useRef<HTMLInputElement>(null);
    const bottomFieldRef = useRef<HTMLInputElement>(null);
    const leftFieldRef = useRef<HTMLInputElement>(null);

    // Get individual properties from composite
    const getMarginProperties = useCallback(() => {
        const properties = marginProp.getProperties();
        return {
            top: properties.find(p => p.get('property') === 'margin-top'),
            right: properties.find(p => p.get('property') === 'margin-right'),
            bottom: properties.find(p => p.get('property') === 'margin-bottom'),
            left: properties.find(p => p.get('property') === 'margin-left'),
        };
    }, [marginProp]);

    const getPaddingProperties = useCallback(() => {
        const properties = paddingProp.getProperties();
        return {
            top: properties.find(p => p.get('property') === 'padding-top'),
            right: properties.find(p => p.get('property') === 'padding-right'),
            bottom: properties.find(p => p.get('property') === 'padding-bottom'),
            left: properties.find(p => p.get('property') === 'padding-left'),
        };
    }, [paddingProp]);

    const getMarginValues = useCallback(() => {
        const props = getMarginProperties();
        return {
            top: {
                value: props.top?.getValue() || "",
                unit: (props.top as any)?.getUnit() || "px"
            },
            right: {
                value: props.right?.getValue() || "",
                unit: (props.right as any)?.getUnit() || "px"
            },
            bottom: {
                value: props.bottom?.getValue() || "",
                unit: (props.bottom as any)?.getUnit() || "px"
            },
            left: {
                value: props.left?.getValue() || "",
                unit: (props.left as any)?.getUnit() || "px"
            },
        };
    }, [getMarginProperties]);

    const getPaddingValues = useCallback(() => {
        const props = getPaddingProperties();
        return {
            top: {
                value: props.top?.getValue() || "",
                unit: (props.top as any)?.getUnit() || "px"
            },
            right: {
                value: props.right?.getValue() || "",
                unit: (props.right as any)?.getUnit() || "px"
            },
            bottom: {
                value: props.bottom?.getValue() || "",
                unit: (props.bottom as any)?.getUnit() || "px"
            },
            left: {
                value: props.left?.getValue() || "",
                unit: (props.left as any)?.getUnit() || "px"
            },
        };
    }, [getPaddingProperties]);



    // Get current values from props
    const marginData = getMarginValues();
    const paddingData = getPaddingValues();

    const handleMarginChange = useCallback((side: 'top' | 'right' | 'bottom' | 'left', value: string) => {
        const props = getMarginProperties();
        const prop = props[side];
        if (prop) {
            if (!value) {
                prop.clear();
            } else {
                prop.upValue(value);
            }
        }
    }, [getMarginProperties]);

    const handlePaddingChange = useCallback((side: 'top' | 'right' | 'bottom' | 'left', value: string) => {
        const props = getPaddingProperties();
        const prop = props[side];
        if (prop) {
            if (!value) {
                prop.clear();
            } else {
                prop.upValue(value);
            }
        }
    }, [getPaddingProperties]);

    const handleMarginUnitChange = useCallback((side: 'top' | 'right' | 'bottom' | 'left', unit: string) => {
        const props = getMarginProperties();
        const prop = props[side];
        if (prop) {
            (prop as any).upUnit(unit);
        }
    }, [getMarginProperties]);

    const handlePaddingUnitChange = useCallback((side: 'top' | 'right' | 'bottom' | 'left', unit: string) => {
        const props = getPaddingProperties();
        const prop = props[side];
        if (prop) {
            (prop as any).upUnit(unit);
        }
    }, [getPaddingProperties]);



    const handleSideClick = useCallback((side: 'margin' | 'padding', position: 'top' | 'right' | 'bottom' | 'left') => {
        setActiveSide(side);
        setActivePosition(position);

        // Focus the corresponding text field
        setTimeout(() => {
            if (position === 'top' && topFieldRef.current) {
                topFieldRef.current.focus();
            } else if (position === 'right' && rightFieldRef.current) {
                rightFieldRef.current.focus();
            } else if (position === 'bottom' && bottomFieldRef.current) {
                bottomFieldRef.current.focus();
            } else if (position === 'left' && leftFieldRef.current) {
                leftFieldRef.current.focus();
            }
        }, 10);
    }, []);



    return (
        <Stack spacing={2} >
            <Typography color="secondary" gutterBottom>
                Box Model
            </Typography>
            <Box px={2}>
                <Box sx={{ position: 'relative', width: '100%', height: 200 }}>
                    {/* Margin Box */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 20,
                            left: 20,
                            right: 20,
                            bottom: 20,
                            border: `2px solid ${theme.palette.warning.main}`,
                            backgroundColor: theme.palette.warning.light,
                            opacity: 0.3,
                        }}
                    />

                    {/* Padding Box */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 40,
                            left: 40,
                            right: 40,
                            bottom: 40,
                            border: `2px solid ${theme.palette.info.main}`,
                            backgroundColor: theme.palette.info.light,
                            opacity: 0.3,
                        }}
                    />

                    {/* Content Box */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 60,
                            left: 60,
                            right: 60,
                            bottom: 60,
                            border: `2px solid ${theme.palette.primary.main}`,
                            backgroundColor: theme.palette.primary.light,
                            opacity: 0.3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            color: theme.palette.primary.contrastText,
                        }}
                    >
                        Content
                    </Box>

                    {/* Margin Labels */}
                    <BoxSide
                        label={`${marginData.top.value}`}
                        isActive={activeSide === 'margin' && activePosition === 'top'}
                        position="top"
                        type="margin"
                        handleSideClick={handleSideClick}
                    />
                    <BoxSide
                        label={`${marginData.right.value}`}
                        isActive={activeSide === 'margin' && activePosition === 'right'}
                        position="right"
                        type="margin"
                        handleSideClick={handleSideClick}
                    />
                    <BoxSide
                        label={`${marginData.bottom.value}`}
                        isActive={activeSide === 'margin' && activePosition === 'bottom'}
                        position="bottom"
                        type="margin"
                        handleSideClick={handleSideClick}
                    />
                    <BoxSide
                        label={`${marginData.left.value}`}
                        isActive={activeSide === 'margin' && activePosition === 'left'}
                        position="left"
                        type="margin"
                        handleSideClick={handleSideClick}
                    />

                    {/* Padding Labels */}
                    <BoxSide
                        label={`${paddingData.top.value}`}
                        isActive={activeSide === 'padding' && activePosition === 'top'}
                        position="top"
                        type="padding"
                        handleSideClick={handleSideClick}
                    />
                    <BoxSide
                        label={`${paddingData.right.value}`}
                        isActive={activeSide === 'padding' && activePosition === 'right'}
                        position="right"
                        type="padding"
                        handleSideClick={handleSideClick}
                    />
                    <BoxSide
                        label={`${paddingData.bottom.value}`}
                        isActive={activeSide === 'padding' && activePosition === 'bottom'}
                        position="bottom"
                        type="padding"
                        handleSideClick={handleSideClick}
                    />
                    <BoxSide
                        label={`${paddingData.left.value}`}
                        isActive={activeSide === 'padding' && activePosition === 'left'}
                        position="left"
                        type="padding"
                        handleSideClick={handleSideClick}
                    />
                </Box>
            </Box>
            {/* Input Fields */}
            {activeSide && (
                <Stack >
                    <Typography variant="caption" color="primary" mb={2}>
                        Editing {activeSide === 'margin' ? 'Margin' : 'Padding'}
                    </Typography>
                    <Stack spacing={2}>
                        <DebouncedTextField
                            label="Top"
                            value={activeSide === 'margin' ? marginData.top.value : paddingData.top.value}
                            onChange={(value) => {
                                activeSide === 'margin' ? handleMarginChange('top', value) : handlePaddingChange('top', value);
                            }}
                            size="small"
                            sx={{ flex: 1 }}
                            inputProps={{ ref: topFieldRef }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <TextField
                                            select
                                            value={activeSide === 'margin' ? marginData.top.unit : paddingData.top.unit}
                                            onChange={(e) => {
                                                activeSide === 'margin' ? handleMarginUnitChange('top', e.target.value) : handlePaddingUnitChange('top', e.target.value);
                                            }}
                                            size="small"
                                            variant="standard"
                                            sx={{
                                                width: 40,
                                                "& .MuiSelect-select": {
                                                    padding: 0,
                                                    textAlign: "center",
                                                },
                                            }}
                                        >
                                            {["px", "%", "em", "rem"].map((unitOption) => (
                                                <MenuItem key={unitOption} value={unitOption}>
                                                    {unitOption}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <DebouncedTextField
                            label="Right"
                            value={activeSide === 'margin' ? marginData.right.value : paddingData.right.value}
                            onChange={(value) => {
                                activeSide === 'margin' ? handleMarginChange('right', value) : handlePaddingChange('right', value);
                            }}
                            size="small"
                            sx={{ flex: 1 }}
                            inputProps={{ ref: rightFieldRef }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <TextField
                                            select
                                            value={activeSide === 'margin' ? marginData.right.unit : paddingData.right.unit}
                                            onChange={(e) => {
                                                activeSide === 'margin' ? handleMarginUnitChange('right', e.target.value) : handlePaddingUnitChange('right', e.target.value);
                                            }}
                                            size="small"
                                            variant="standard"
                                            sx={{
                                                width: 40,
                                                "& .MuiSelect-select": {
                                                    padding: 0,
                                                    textAlign: "center",
                                                },
                                            }}
                                        >
                                            {["px", "%", "em", "rem"].map((unitOption) => (
                                                <MenuItem key={unitOption} value={unitOption}>
                                                    {unitOption}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <DebouncedTextField
                            label="Bottom"
                            value={activeSide === 'margin' ? marginData.bottom.value : paddingData.bottom.value}
                            onChange={(value) => {
                                activeSide === 'margin' ? handleMarginChange('bottom', value) : handlePaddingChange('bottom', value);
                            }}
                            size="small"
                            sx={{ flex: 1 }}
                            inputProps={{ ref: bottomFieldRef }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <TextField
                                            select
                                            value={activeSide === 'margin' ? marginData.bottom.unit : paddingData.bottom.unit}
                                            onChange={(e) => {
                                                activeSide === 'margin' ? handleMarginUnitChange('bottom', e.target.value) : handlePaddingUnitChange('bottom', e.target.value);
                                            }}
                                            size="small"
                                            variant="standard"
                                            sx={{
                                                width: 40,
                                                "& .MuiSelect-select": {
                                                    padding: 0,
                                                    textAlign: "center",
                                                },
                                            }}
                                        >
                                            {["px", "%", "em", "rem"].map((unitOption) => (
                                                <MenuItem key={unitOption} value={unitOption}>
                                                    {unitOption}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <DebouncedTextField
                            label="Left"
                            value={activeSide === 'margin' ? marginData.left.value : paddingData.left.value}
                            onChange={(value) => {
                                activeSide === 'margin' ? handleMarginChange('left', value) : handlePaddingChange('left', value);
                            }}
                            size="small"
                            sx={{ flex: 1 }}
                            inputProps={{ ref: leftFieldRef }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <TextField
                                            select
                                            value={activeSide === 'margin' ? marginData.left.unit : paddingData.left.unit}
                                            onChange={(e) => {
                                                activeSide === 'margin' ? handleMarginUnitChange('left', e.target.value) : handlePaddingUnitChange('left', e.target.value);
                                            }}
                                            size="small"
                                            variant="standard"
                                            sx={{
                                                width: 40,
                                                "& .MuiSelect-select": {
                                                    padding: 0,
                                                    textAlign: "center",
                                                },
                                            }}
                                        >
                                            {["px", "%", "em", "rem"].map((unitOption) => (
                                                <MenuItem key={unitOption} value={unitOption}>
                                                    {unitOption}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

export default BoxModelEditor; 